import { useQuery, useQueryClient } from 'react-query';

import { fetchTimelineData, TimelineSortRule } from '../services';
import {
  getTimelineAuxiliaryCount,
  getTimelineRecordsFromResponse,
  getTimelineToday,
  getTimelineTomorrow,
  TimelineAuxiliaryCount,
  TimelineFilters,
  TimelineSortOption,
  TimelineTab,
} from '../timelineData';

interface UseTimelineQueriesProps {
  hasPermission: boolean;
  operationId?: string;
  userId: number | null;
  activeTab: TimelineTab;
  pageIndex: number;
  pageSize: number;
  search: string;
  isSearchPending: boolean;
  filters: TimelineFilters;
  sort: TimelineSortOption;
}

const getSortRules = (sort: TimelineSortOption): TimelineSortRule[] => {
  switch (sort) {
    case 'recent':
      return [{ id: 'datareferencia', desc: true }];
    case 'oldest':
      return [{ id: 'datareferencia', desc: false }];
    case 'materiality':
    default:
      return [{ id: 'materiality', desc: true }];
  }
};

export const getSharedFilters = (
  search: string,
  filters: TimelineFilters,
  operationId?: string,
) => ({
  ...(operationId ? { operationId } : {}),
  ...(search.trim() ? { search: search.trim() } : {}),
  ...(!operationId && filters.markets.length
    ? { markets: filters.markets }
    : {}),
  ...(filters.categories.length ? { categories: filters.categories } : {}),
  ...(filters.types.length ? { types: filters.types } : {}),
  ...(filters.species.length ? { species: filters.species } : {}),
  ...(filters.themes.length ? { themes: filters.themes } : {}),
  ...(filters.relevances.length ? { relevances: filters.relevances } : {}),
  ...(filters.dateStart ? { dateStart: filters.dateStart } : {}),
  ...(filters.dateEnd ? { dateEnd: filters.dateEnd } : {}),
  ...(!operationId && filters.favorites ? { favorites: true } : {}),
});

const emptyResponse = (page: number, pageSize: number) => ({
  data: [],
  meta: { pagination: { page, pageSize, pageCount: 0, total: 0 } },
});

const getNormalizedPage = (
  requestedPage: number,
  pageCount?: number,
  total?: number,
) => {
  if (
    typeof pageCount !== 'number' ||
    !Number.isSafeInteger(pageCount) ||
    pageCount < 0 ||
    typeof total !== 'number' ||
    !Number.isFinite(total) ||
    total < 0
  ) {
    return undefined;
  }

  if (total === 0 || pageCount === 0) return 1;

  return requestedPage > pageCount ? pageCount : requestedPage;
};

export default function useTimelineQueries({
  hasPermission,
  operationId,
  userId,
  activeTab,
  pageIndex,
  pageSize,
  search,
  isSearchPending,
  filters,
  sort,
}: UseTimelineQueriesProps) {
  const queryClient = useQueryClient();
  const operationScope = operationId?.trim() || null;
  const today = getTimelineToday();
  const tomorrow = getTimelineTomorrow();
  const sharedFilters = getSharedFilters(search, filters, operationId);
  const highlightsDateEnd =
    filters.dateEnd && filters.dateEnd < today ? filters.dateEnd : today;
  const highlightsFilters = {
    ...sharedFilters,
    relevances: ['Alta'],
    dateEnd: highlightsDateEnd,
  };
  const occurredFilters = {
    ...sharedFilters,
    dateEnd: highlightsDateEnd,
  };
  const futureDateStart =
    filters.dateStart && filters.dateStart > tomorrow
      ? filters.dateStart
      : tomorrow;
  const futureFilters = {
    ...sharedFilters,
    dateStart: futureDateStart,
  };
  const highlightsCannotMatch =
    (filters.relevances.length > 0 && !filters.relevances.includes('Alta')) ||
    Boolean(filters.dateStart && filters.dateStart > today);
  const futureCannotMatch =
    (Boolean(filters.dateStart && filters.dateEnd) &&
      filters.dateStart > filters.dateEnd) ||
    Boolean(filters.dateEnd && filters.dateEnd < futureDateStart);
  const dateRangeCannotMatch =
    Boolean(filters.dateStart && filters.dateEnd) &&
    filters.dateStart > filters.dateEnd;
  const tabFilters =
    activeTab === 'highlights'
      ? highlightsFilters
      : activeTab === 'future'
        ? futureFilters
        : sharedFilters;
  const activeTabCannotMatch =
    dateRangeCannotMatch ||
    (activeTab === 'highlights' && highlightsCannotMatch) ||
    (activeTab === 'future' && futureCannotMatch);
  const sortRules = getSortRules(sort);
  const criteriaKey = JSON.stringify({
    operationId: operationScope,
    userId,
    activeTab,
    filters: tabFilters,
    sort: sortRules,
    today: activeTab === 'future' ? today : undefined,
  });
  const activeRequest = {
    ...tabFilters,
    sort: sortRules,
  };

  const timelineQuery = useQuery({
    queryKey: [
      'timeline-data',
      activeTab,
      operationScope,
      userId,
      pageIndex,
      pageSize,
      criteriaKey,
      Boolean(tabFilters.favorites),
    ],
    queryFn: async ({ signal }) => ({
      criteriaKey,
      operationScope,
      pageIndex,
      pageSize,
      userId,
      response: await fetchTimelineData({
        ...activeRequest,
        page: pageIndex + 1,
        pageSize,
        signal,
      }),
    }),
    onSuccess: ({ response }) => {
      if (
        activeTab === 'highlights' &&
        typeof response.meta?.pagination?.total === 'number'
      ) {
        queryClient.setQueryData(
          [
            'timeline-count',
            'highlights',
            operationScope,
            userId,
            highlightsFilters,
          ],
          response,
        );
      }
    },
    enabled: hasPermission && !activeTabCannotMatch && !isSearchPending,
    keepPreviousData: true,
  });

  const baseCountQuery = useQuery({
    queryKey: ['timeline-count', 'base', operationScope, userId],
    queryFn: ({ signal }) =>
      fetchTimelineData({
        ...(operationId ? { operationId } : {}),
        page: 1,
        pageSize: 1,
        signal,
      }),
    enabled: hasPermission,
    staleTime: 30_000,
  });

  const highlightsCountQuery = useQuery({
    queryKey: [
      'timeline-count',
      'highlights',
      operationScope,
      userId,
      highlightsFilters,
    ],
    queryFn: ({ signal }) =>
      fetchTimelineData({
        ...highlightsFilters,
        page: 1,
        pageSize: 1,
        signal,
      }),
    enabled:
      hasPermission &&
      activeTab !== 'highlights' &&
      !highlightsCannotMatch &&
      !isSearchPending,
    staleTime: 30_000,
  });

  const futureCountQuery = useQuery({
    queryKey: [
      'timeline-count',
      'future',
      operationScope,
      userId,
      sharedFilters,
      today,
    ],
    queryFn: ({ signal }) =>
      fetchTimelineData({
        ...futureFilters,
        page: 1,
        pageSize: 1,
        signal,
      }),
    enabled:
      hasPermission &&
      activeTab !== 'future' &&
      !futureCannotMatch &&
      !isSearchPending,
    staleTime: 30_000,
  });

  const allCountQuery = useQuery({
    queryKey: ['timeline-count', 'all', operationScope, userId, sharedFilters],
    queryFn: ({ signal }) =>
      fetchTimelineData({
        ...sharedFilters,
        page: 1,
        pageSize: 1,
        signal,
      }),
    enabled:
      hasPermission &&
      activeTab !== 'all' &&
      !dateRangeCannotMatch &&
      !isSearchPending,
    staleTime: 30_000,
  });

  const occurredCountQuery = useQuery({
    queryKey: [
      'timeline-count',
      'occurred',
      operationScope,
      userId,
      occurredFilters,
    ],
    queryFn: ({ signal }) =>
      fetchTimelineData({
        ...occurredFilters,
        page: 1,
        pageSize: 1,
        signal,
      }),
    enabled:
      hasPermission &&
      activeTab === 'highlights' &&
      !dateRangeCannotMatch &&
      !(filters.dateStart && filters.dateStart > highlightsDateEnd) &&
      !isSearchPending,
    staleTime: 30_000,
  });

  const isError =
    !isSearchPending && !activeTabCannotMatch && timelineQuery.isError;
  const isSameIdentityData =
    timelineQuery.data?.operationScope === operationScope &&
    timelineQuery.data?.userId === userId;
  const hasCurrentCriteria =
    !isSearchPending && timelineQuery.data?.criteriaKey === criteriaKey;
  const hasCurrentPage =
    timelineQuery.data?.pageIndex === pageIndex &&
    timelineQuery.data?.pageSize === pageSize;
  const hasCriteriaMismatch =
    isSearchPending || !hasCurrentCriteria || !hasCurrentPage;
  const hasPreviousRecords = Boolean(
    isSameIdentityData && (timelineQuery.data?.response.data.length ?? 0) > 0,
  );
  const isCriteriaTransitioning =
    !activeTabCannotMatch &&
    !isError &&
    hasCriteriaMismatch &&
    hasPreviousRecords;
  const activeResponse = activeTabCannotMatch
    ? emptyResponse(pageIndex + 1, pageSize)
    : isError
      ? undefined
      : isCriteriaTransitioning
        ? timelineQuery.data?.response
        : hasCurrentCriteria && hasCurrentPage
          ? timelineQuery.data?.response
          : undefined;
  const isCurrentTabLoading = isSearchPending
    ? true
    : activeTabCannotMatch
      ? false
      : timelineQuery.isLoading ||
        (!hasCurrentCriteria && !timelineQuery.isError);
  const showListSkeleton =
    !activeTabCannotMatch &&
    !isError &&
    (isCurrentTabLoading || hasCriteriaMismatch) &&
    !isCriteriaTransitioning;
  const hasCurrentResponse =
    hasCurrentCriteria &&
    !timelineQuery.isFetching &&
    !timelineQuery.isPreviousData &&
    !timelineQuery.isError &&
    timelineQuery.data?.pageIndex === pageIndex &&
    timelineQuery.data?.pageSize === pageSize;
  const hasSettledImpossibleCriteria =
    activeTabCannotMatch &&
    !isSearchPending &&
    !timelineQuery.isFetching &&
    !timelineQuery.isError;
  const paginationForNormalization = hasSettledImpossibleCriteria
    ? emptyResponse(pageIndex + 1, pageSize).meta.pagination
    : hasCurrentResponse
      ? timelineQuery.data?.response.meta?.pagination
      : undefined;
  const normalizedPage = paginationForNormalization
    ? getNormalizedPage(
        pageIndex + 1,
        paginationForNormalization.pageCount,
        paginationForNormalization.total,
      )
    : undefined;
  const total = getTimelineAuxiliaryCount({
    data: activeResponse,
    isLoading: isCurrentTabLoading,
    isError,
  });
  const baseTotal = getTimelineAuxiliaryCount({
    data: baseCountQuery.data,
    isLoading: baseCountQuery.isLoading,
    isError: baseCountQuery.isError,
  });
  const highlightsAuxiliaryCount = highlightsCannotMatch
    ? 0
    : isSearchPending
      ? 'loading'
      : getTimelineAuxiliaryCount({
          data: highlightsCountQuery.data,
          isLoading: highlightsCountQuery.isLoading,
          isError: highlightsCountQuery.isError,
        });
  const futureAuxiliaryCount = futureCannotMatch
    ? 0
    : isSearchPending
      ? 'loading'
      : getTimelineAuxiliaryCount({
          data: futureCountQuery.data,
          isLoading: futureCountQuery.isLoading,
          isError: futureCountQuery.isError,
        });
  const allAuxiliaryCount = dateRangeCannotMatch
    ? 0
    : isSearchPending
      ? 'loading'
      : getTimelineAuxiliaryCount({
          data: allCountQuery.data,
          isLoading: allCountQuery.isLoading,
          isError: allCountQuery.isError,
        });
  const tabCounts: Record<TimelineTab, TimelineAuxiliaryCount> = {
    all: activeTab === 'all' ? total : allAuxiliaryCount,
    highlights: activeTab === 'highlights' ? total : highlightsAuxiliaryCount,
    future: activeTab === 'future' ? total : futureAuxiliaryCount,
  };
  const occurredCount =
    dateRangeCannotMatch ||
    Boolean(filters.dateStart && filters.dateStart > highlightsDateEnd)
      ? 0
      : isSearchPending
        ? 'loading'
        : getTimelineAuxiliaryCount({
            data: occurredCountQuery.data,
            isLoading:
              occurredCountQuery.isLoading || occurredCountQuery.isFetching,
            isError: occurredCountQuery.isError,
          });

  const cancelAuxiliaryCount = (tab: TimelineTab) => {
    void queryClient.cancelQueries(['timeline-count', tab, operationScope]);
  };
  const records = getTimelineRecordsFromResponse({
    records: activeResponse?.data,
    isError,
  });
  const displayedPage = hasCurrentCriteria
    ? (timelineQuery.data?.pageIndex ?? pageIndex) + 1
    : pageIndex + 1;
  const displayedPageSize = hasCurrentCriteria
    ? timelineQuery.data?.pageSize ?? pageSize
    : pageSize;

  return {
    baseTotal,
    cancelAuxiliaryCount,
    displayedPage,
    displayedPageSize,
    isError,
    isCriteriaTransitioning,
    isFetching:
      !isSearchPending && !activeTabCannotMatch && timelineQuery.isFetching,
    isLoading: isCurrentTabLoading,
    normalizedPage,
    occurredCount,
    records,
    retry: timelineQuery.refetch,
    showListSkeleton,
    tabCounts,
    total,
  };
}
